import { ApiService } from '@/support/ApiService';
import type { components } from '@/generated/internal-api/openapi-types';

type StudyApiId = components['schemas']['StudyApiId.schema'];
type AgentSessionApiId = components['schemas']['AgentSessionApiId.schema'];

/**
 * Thin client for the agent kickoff/cancel/get endpoints exposed by the
 * internal API. The streaming side runs over SSE — see useAgentStream.
 */
export class AgentSessionService {
  static createStudyHealthCheckSession(body: { study_api_id: StudyApiId }, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/study-health-check',
      body,
      signal,
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'agent-session-create-study-health-check' },
      },
    });
  }

  static createStudyProtocolParserSession(body: { url: string }, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/study-protocol-parser',
      body,
      signal,
      options: {
        onError: {
          toast: false,
          throw: false,
          capture: true,
          slug: 'agent-session-create-study-protocol-parser',
        },
      },
    });
  }

  static presignStudyProtocolParserUpload(body: { file_name: string }, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/study-protocol-parser/uploads',
      body,
      signal,
      options: {
        onError: {
          toast: false,
          throw: false,
          capture: true,
          slug: 'agent-session-presign-study-protocol-parser-upload',
        },
      },
    });
  }

  static getSession(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}',
      path: { sessionApiId },
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-get' } },
    });
  }

  static cancelSession(sessionApiId: AgentSessionApiId, signal?: AbortSignal) {
    return ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/sessions/{sessionApiId}/cancel',
      path: { sessionApiId },
      signal,
      options: { onError: { toast: false, throw: false, capture: true, slug: 'agent-session-cancel' } },
    });
  }
}
